import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch, removeItem } from '@ngxs/store/operators';
import { ApplicationFilesService } from '@tran-monorepo/frontend/services';
import { downloadFileByUrl } from '@tran-monorepo/frontend/utils';
import { Page, PageCursorMetadata } from '@tran-monorepo/interfaces';
import { Application, AwardCommitteeMeeting, Kpi } from '@tran-monorepo/interfaces/applications';
import { File as FileData } from '@tran-monorepo/interfaces/files';
import { catchError, EMPTY, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { AwardCommitteeService } from '../../deadlines/services/award-committee.service';
import { ManagementApplicationDetailStateModel } from '../interfaces/application-detail-state.interface';
import { ApplicationsService } from '../services/applications.service';
import {
    DeleteManagementAttachment,
    DownloadManagementAttachment,
    LoadAwardCommitteeMeetings,
    LoadManagementApplication,
    LoadManagementApplicationAttachments,
    LoadMoreManagementApplicationAttachments,
    LoadSelectedAwardCommitteeMeeting,
    ResetApplicationDetailState,
    UpdateManagementApplication,
    UploadManagementAttachment
} from './application-detail.actions';

const DEFAULT_STATE: ManagementApplicationDetailStateModel = {
    application: {
        value: null,
        isLoading: false
    },
    applicationKpis: [],
    attachments: {
        items: [],
        metadata: null,
        limit: 20,
        isLoading: false,
        isUploading: false,
        isDeleting: false
    },
    selectedAwardCommitteeMeeting: {
        value: null,
        isLoading: false
    },
    awardCommitteeMeetings: {
        items: [],
        isLoading: false,
        hasNextPage: true
    }
};

@State<ManagementApplicationDetailStateModel>({
    name: 'managementApplicationDetailState',
    defaults: DEFAULT_STATE
})
@Injectable()
export class ManagementApplicationDetailState {
    private readonly applicationsService = inject(ApplicationsService);
    private readonly applicationsFileService = inject(ApplicationFilesService);
    private readonly awardCommitteeService = inject(AwardCommitteeService);

    @Selector()
    public static application(state: ManagementApplicationDetailStateModel): Application | null {
        return state?.application?.value ?? null;
    }

    @Selector()
    public static isLoadingApplication(state: ManagementApplicationDetailStateModel): boolean {
        return state.application.isLoading;
    }

    @Selector()
    public static applicationKpis(state: ManagementApplicationDetailStateModel): Kpi[] {
        return state.applicationKpis;
    }

    @Selector()
    public static attachments(state: ManagementApplicationDetailStateModel): FileData[] {
        return state.attachments.items;
    }

    @Selector()
    public static isLoadingAttachments(state: ManagementApplicationDetailStateModel): boolean {
        return state.attachments.isLoading;
    }

    @Selector()
    public static isUploadingAttachments(state: ManagementApplicationDetailStateModel): boolean {
        return state.attachments.isUploading;
    }

    @Selector()
    public static isDeletingAttachments(state: ManagementApplicationDetailStateModel): boolean {
        return state.attachments.isDeleting;
    }

    @Selector()
    public static attachmentsMetadata(state: ManagementApplicationDetailStateModel): PageCursorMetadata | null {
        return state.attachments.metadata;
    }

    @Selector()
    public static selectedAwardCommitteeMeeting(
        state: ManagementApplicationDetailStateModel
    ): AwardCommitteeMeeting | null {
        return state.selectedAwardCommitteeMeeting.value;
    }

    @Selector()
    public static isSelectedAwardCommitteeMeetingLoading(state: ManagementApplicationDetailStateModel): boolean {
        return state.selectedAwardCommitteeMeeting.isLoading;
    }

    @Selector()
    public static awardCommitteeMeetings(state: ManagementApplicationDetailStateModel): AwardCommitteeMeeting[] {
        return state.awardCommitteeMeetings.items;
    }

    @Selector()
    public static isAwardCommitteeMeetingsLoading(state: ManagementApplicationDetailStateModel): boolean {
        return state.awardCommitteeMeetings.isLoading;
    }

    @Selector()
    public static hasAwardCommitteeMeetingsNextPage(state: ManagementApplicationDetailStateModel): boolean {
        return state.awardCommitteeMeetings.hasNextPage;
    }

    @Action(LoadManagementApplication)
    public loadApplication(
        { setState }: StateContext<ManagementApplicationDetailStateModel>,
        { applicationId }: LoadManagementApplication
    ): Observable<Application> {
        setState(
            patch<ManagementApplicationDetailStateModel>({
                application: patch({
                    isLoading: true
                })
            })
        );
        return forkJoin({
            application: this.applicationsService.getApplication(applicationId),
            applicationKpis: this.applicationsService.getApplicationKpis(applicationId)
        }).pipe(
            tap(({ application, applicationKpis }) =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        application: patch({
                            value: application
                        }),
                        applicationKpis
                    })
                )
            ),
            map(({ application }) => application),
            finalize(() =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        application: patch({
                            isLoading: false
                        })
                    })
                )
            )
        );
    }

    @Action(UpdateManagementApplication)
    public updateApplication(
        { setState, dispatch }: StateContext<ManagementApplicationDetailStateModel>,
        { applicationId, application, status }: UpdateManagementApplication
    ): Observable<Application> {
        setState(
            patch<ManagementApplicationDetailStateModel>({
                application: patch({
                    isLoading: true
                })
            })
        );
        return this.applicationsService.updateApplication(applicationId, application).pipe(
            switchMap(value => {
                if (status && status !== value.status) {
                    return this.applicationsService.transitionStatus(applicationId, status);
                }
                return of(value);
            }),
            tap(value =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        application: patch({
                            value
                        })
                    })
                )
            ),
            tap(() => dispatch(new LoadSelectedAwardCommitteeMeeting())),
            finalize(() =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        application: patch({
                            isLoading: false
                        })
                    })
                )
            )
        );
    }

    @Action(LoadManagementApplicationAttachments)
    public loadAttachments(
        { setState, getState }: StateContext<ManagementApplicationDetailStateModel>,
        { payload: { pageSize } }: LoadManagementApplicationAttachments
    ): Observable<Page<FileData, PageCursorMetadata>> {
        setState(
            patch<ManagementApplicationDetailStateModel>({
                attachments: patch({
                    isLoading: true,
                    limit: pageSize
                })
            })
        );

        const {
            application: { value: application }
        } = getState();

        if (!application?.id) {
            setState(
                patch<ManagementApplicationDetailStateModel>({
                    attachments: patch({
                        isLoading: false
                    })
                })
            );
            return EMPTY;
        }

        return this.applicationsFileService.listFiles(application.id, undefined, pageSize).pipe(
            tap(({ items, metadata }) =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            items,
                            metadata
                        })
                    })
                )
            ),
            catchError(() => {
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            items: [],
                            metadata: null
                        })
                    })
                );
                return EMPTY;
            }),
            finalize(() =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            isLoading: false
                        })
                    })
                )
            )
        );
    }

    @Action(LoadMoreManagementApplicationAttachments)
    public loadMoreAttachments({
        setState,
        getState
    }: StateContext<ManagementApplicationDetailStateModel>): Observable<Page<FileData, PageCursorMetadata>> | void {
        const {
            attachments: { isLoading, metadata, limit },
            application: { value: application }
        } = getState();

        if (isLoading || !metadata?.cursor) {
            return;
        }

        setState(
            patch<ManagementApplicationDetailStateModel>({
                attachments: patch({
                    isLoading: true
                })
            })
        );

        if (!application?.id) {
            setState(
                patch<ManagementApplicationDetailStateModel>({
                    attachments: patch({
                        isLoading: false
                    })
                })
            );
            return EMPTY;
        }

        return this.applicationsFileService.listFiles(application.id, metadata.cursor, limit).pipe(
            tap(({ items: newAttachments, metadata }) =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            items: append<FileData>(newAttachments),
                            metadata
                        })
                    })
                )
            ),

            catchError(() => {
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            isLoading: false
                        })
                    })
                );
                return EMPTY;
            }),
            finalize(() =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            isLoading: false
                        })
                    })
                )
            )
        );
    }

    @Action(DownloadManagementAttachment)
    public downloadAttachment(
        ctx: StateContext<ManagementApplicationDetailStateModel>,
        { fileId }: DownloadManagementAttachment
    ): Observable<void> {
        const {
            application: { value: application },
            attachments: { items: attachments }
        } = ctx.getState();

        if (!application?.id) {
            return EMPTY;
        }

        const downloadFileName = attachments.find(file => file.id === fileId)?.name;

        return this.applicationsFileService.getFileDownloadUrl(application.id, fileId, downloadFileName).pipe(
            tap(({ signedUrl }) => downloadFileByUrl(signedUrl)),
            map(() => void 0)
        );
    }

    @Action(UploadManagementAttachment)
    public uploadAttachment(
        ctx: StateContext<ManagementApplicationDetailStateModel>,
        { payload: { applicationId, fileData, file } }: UploadManagementAttachment
    ): Observable<FileData> {
        ctx.setState(
            patch<ManagementApplicationDetailStateModel>({
                attachments: patch({
                    isUploading: true
                })
            })
        );
        return this.applicationsFileService.createFileData(applicationId, fileData, file).pipe(
            tap(uploadedFile =>
                ctx.setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            items: append<FileData>([uploadedFile])
                        })
                    })
                )
            ),
            finalize(() =>
                ctx.setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            isUploading: false
                        })
                    })
                )
            )
        );
    }

    @Action(DeleteManagementAttachment)
    public deleteAttachment(
        ctx: StateContext<ManagementApplicationDetailStateModel>,
        { payload: { applicationId, fileId } }: DeleteManagementAttachment
    ): Observable<void> {
        ctx.setState(
            patch<ManagementApplicationDetailStateModel>({
                attachments: patch({
                    isDeleting: true
                })
            })
        );
        return this.applicationsFileService.deleteFile(applicationId, fileId).pipe(
            tap(() =>
                ctx.setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            items: removeItem<FileData>(file => file.id === fileId)
                        })
                    })
                )
            ),
            finalize(() =>
                ctx.setState(
                    patch<ManagementApplicationDetailStateModel>({
                        attachments: patch({
                            isDeleting: false
                        })
                    })
                )
            )
        );
    }

    @Action(ResetApplicationDetailState)
    public resetState(ctx: StateContext<ManagementApplicationDetailStateModel>): void {
        ctx.setState(DEFAULT_STATE);
    }

    @Action(LoadAwardCommitteeMeetings)
    public loadAwardCommitteeMeetings(
        ctx: StateContext<ManagementApplicationDetailStateModel>
    ): Observable<AwardCommitteeMeeting[]> {
        const {
            awardCommitteeMeetings: { currentPage, hasNextPage }
        } = ctx.getState();
        if (!hasNextPage) {
            return EMPTY;
        }
        const nextPage = currentPage ? currentPage + 1 : 0;
        ctx.setState(
            patch<ManagementApplicationDetailStateModel>({
                awardCommitteeMeetings: patch({
                    isLoading: true
                })
            })
        );
        return this.awardCommitteeService.getMeetings(nextPage, undefined, 'startDate', 'asc').pipe(
            tap(({ items, metadata }) => {
                const { currentPage, hasNextPage } = metadata;
                ctx.setState(
                    patch<ManagementApplicationDetailStateModel>({
                        awardCommitteeMeetings: patch({ items: append(items), currentPage, hasNextPage })
                    })
                );
            }),
            map(({ items }) => items),
            finalize(() =>
                ctx.setState(
                    patch<ManagementApplicationDetailStateModel>({
                        awardCommitteeMeetings: patch({
                            isLoading: false
                        })
                    })
                )
            )
        );
    }

    @Action(LoadSelectedAwardCommitteeMeeting)
    public loadSelectedAwardCommitteeMeeting({
        setState,
        getState
    }: StateContext<ManagementApplicationDetailStateModel>): Observable<AwardCommitteeMeeting> {
        const {
            application: { value: application }
        } = getState();
        if (!application?.awardCommitteeMeetingId) {
            return EMPTY;
        }
        setState(
            patch<ManagementApplicationDetailStateModel>({
                selectedAwardCommitteeMeeting: patch({
                    isLoading: true
                })
            })
        );
        return this.awardCommitteeService.getMeetingById(application.awardCommitteeMeetingId).pipe(
            tap(meeting =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        selectedAwardCommitteeMeeting: patch({
                            value: meeting
                        })
                    })
                )
            ),
            finalize(() =>
                setState(
                    patch<ManagementApplicationDetailStateModel>({
                        selectedAwardCommitteeMeeting: patch({
                            isLoading: false
                        })
                    })
                )
            )
        );
    }
}
